import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  ClickAwayListener,
  Grid,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Label,
  LineChart,
  Line,
} from 'recharts';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import {
  COLOR_BLUE_BLACK_LIGHT,
  COLOR_E,
  COLOR_PRIMARY,
  COLOR_TOOLTIP_BACKGROUND,
  DEMO_DASHBOARD_FILTER_GRAPH_TOOLTIP_LTM_DATA,
  exchangeRates,
  shortMonthNames,
} from 'src/utils/const';
import {
  abbreviateNumber,
  convertCurrency,
  formatCurrency,
  getHeadingStyle,
  isEmpty,
} from 'src/utils/functions';
import { LoadingCard } from 'src/App/Dashboards/Market/Components';
import {
  COLOR_GREY_200,
  COLOR_GREY_400,
  COLOR_GREY_600,
  COLOR_PRIMARY_2,
  COLOR_TEXT_2,
  COLOR_TEXT_3,
} from 'src/utils/colors';
import { InfoButton } from 'src/App/Dashboards/Market/Components/Buttons';
import { LTMMonthlyDataType, LTMMonthlyPerformanceBreakdownType } from 'src/interfaces/apiTypes';
import { Portal } from '@material-ui/core';
import { debounce } from 'lodash';
import { logAmplitudeEvent } from 'src/utils/analytics';
import { useTheme } from '@mui/material';

const FilterChip = ({
  title,
  isActive,
  color,
  handleOnClick,
}: {
  title: string;
  isActive: boolean;
  color?: string;
  handleOnClick?: () => void;
}) => {
  color = !isActive ? '#999' : color ? color : COLOR_BLUE_BLACK_LIGHT;

  const style = {
    chip: {
      backgroundColor: isActive ? color : '#fff',
      borderColor: color,
      color: isActive ? '#fff' : color,
    },
    icon: {
      color: isActive ? '#fff' : color,
    },
  };

  return (
    <>
      <Chip
        label={title}
        variant={isActive ? 'filled' : 'outlined'}
        size="small"
        deleteIcon={
          isActive ? (
            <RemoveCircleOutlineRoundedIcon style={style.icon} />
          ) : (
            <AddCircleOutlineRoundedIcon style={style.icon} />
          )
        }
        onClick={() => handleOnClick && handleOnClick()}
        onDelete={() => handleOnClick && handleOnClick()}
        sx={style.chip}
      />
    </>
  );
};

type CatagoryMapType = { [key: string]: { name: string } };
type OptionalCatagoryMapType = { [key: string]: { name: string; isActive: boolean } };

const processDynamicRangeCatagoryMap = (
  rangeObject: any,
  currency: string,
  isCurrency?: boolean
) => {
  const resultObject: CatagoryMapType = {};
  for (const key in rangeObject) {
    resultObject[key] = {
      name: !isCurrency
        ? key
        : key
            .split('-')
            .map(
              data =>
                exchangeRates[currency].symbol +
                abbreviateNumber(convertCurrency(currency, Number(data)), true)
            )
            .join(' - '),
    };
  }
  return resultObject;
};

const PremiumOverlay = ({
  isXAxisTickVertical,
  handleClick,
  offsetX,
  offsetY,
  offsetWidth,
  offsetHeight,
}: {
  isXAxisTickVertical: boolean | undefined;
  handleClick: () => void;
  offsetX?: number;
  offsetY?: number;
  offsetWidth?: number;
  offsetHeight?: number;
}) => {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: offsetY ? offsetY + 'px' : 0,
        left: offsetX ? offsetX + 'px' : '30px',
        width: `calc(100% - 30px + ${offsetWidth ? offsetWidth + 'px' : '0px'})`,
        height: `calc(${isXAxisTickVertical ? '70%' : '80%'} + ${
          offsetHeight ? offsetHeight + 'px' : '0px'
        })`,
        background: '#fffa',
        backgroundBlendMode: 'multiply',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Typography
        variant="body1"
        onClick={handleClick}
        sx={{
          fontSize: '26px',
          fontWeight: 900,
          color: COLOR_E,
          cursor: 'pointer',
        }}
      >
        PREMIUM
      </Typography>
    </Box>
  );
};

const processCurrencyGraphLabelRange = (label: string, currency: string) => {
  return label
    .split('-')
    .map(
      valueUSD =>
        exchangeRates[currency].symbol +
        abbreviateNumber(convertCurrency(currency, Number(valueUSD)))
    )
    .join('-');
};

const SingleBarFilterCard = ({
  title,
  tooltipText,
  catagoryMap,
  optionalCatagoryMap,
  data,
  isLoading,
  metricTabs,
  color,
  currency,
  defaultMetrics,
  isXaxisCurrency,
  xAxisTitle,
  maxBarSize,
  isXAxisTickVertical,
  isDisabled,
  handleOnSampleClick,
  showSelectionChips,
  isGraphSizeSmall,
  showLTMGraphTooltip,
  dynamicTooltipCatagory,
  graphTooltipLTMData,
  isGraphLocked,
  tooltipTitleSuffix,
  titleHighlightType,
  xAxisOrder,
  totalListingsCount,
  parent,
  graphRef,
}: {
  title: string;
  tooltipText: string;
  catagoryMap: CatagoryMapType | 'dynamicRange';
  optionalCatagoryMap?: OptionalCatagoryMapType;
  // data?: {[key: string]: {[key: string] : number}} | null;
  data?: any;
  isLoading: boolean;
  metricTabs: string[];
  color: string;
  currency: string;
  defaultMetrics?: 'occupancyRate' | 'revenue' | 'adr' | 'supply';
  isXaxisCurrency?: boolean;
  xAxisTitle?: string;
  maxBarSize?: number;
  isXAxisTickVertical?: boolean;
  isDisabled: boolean;
  handleOnSampleClick: () => void;
  showSelectionChips?: boolean;
  isGraphSizeSmall?: boolean;
  showLTMGraphTooltip?: boolean;
  dynamicTooltipCatagory?: string[];
  graphTooltipLTMData?: { [key: string]: { [key: string]: LTMMonthlyDataType } } | null;
  isGraphLocked: boolean;
  tooltipTitleSuffix?: string;
  titleHighlightType?: 'prefix' | 'sufix';
  xAxisOrder?: string[];
  totalListingsCount: number;
  parent?: string;
  graphRef?: any;
}) => {
  const theme = useTheme();

  const [catagoryMapState, setCatagoryMapState] = useState<CatagoryMapType>(
    catagoryMap === 'dynamicRange' ? {} : catagoryMap
  );
  const [optionalCatagoryMapState, setOptionalCatagoryMapState] = useState<
    OptionalCatagoryMapType | undefined
  >(optionalCatagoryMap);

  const [tabSelected, setTabSelected] = useState<
    'occupancyRate' | 'adr' | 'revenue' | 'supply' | 'revPanChange'
  >(metricTabs[0] as 'occupancyRate' | 'adr' | 'revenue' | 'supply' | 'revPanChange');

  const [currentBarChartDataOnHover, setCurrentBarChartDataOnHover] = useState<any>(null);

  const [isLTMTooltipHovered, setIsLTMTooltipHovered] = useState<boolean>(false);

  if (!titleHighlightType) {
    titleHighlightType = 'sufix';
  }

  useEffect(() => {
    catagoryMap === 'dynamicRange' &&
      setCatagoryMapState(processDynamicRangeCatagoryMap(data, currency, isXaxisCurrency));
  }, [data, currency]);

  useEffect(() => {
    !currentBarChartDataOnHover && setIsLTMTooltipHovered(false);
  }, [currentBarChartDataOnHover]);

  const processGraphData = (dataObj: any) => {
    const graphResultArray: { [key: string]: string | number }[] = [];

    // xAxisOrder? xAxisOrder :
    (xAxisOrder ? xAxisOrder : Object.keys(dataObj)).forEach(key => {
      catagoryMapState[key] &&
        graphResultArray.push({
          ...dataObj[key],
          name: key,
        });

      optionalCatagoryMapState &&
        optionalCatagoryMapState[key]?.isActive &&
        graphResultArray.push({
          ...dataObj[key],
          name: key,
          isOptionalKey: true,
        });
    });

    return graphResultArray;
  };

  const handleFilterChipClick = (valueClicked: string) => {
    optionalCatagoryMapState &&
      setOptionalCatagoryMapState(prevState => {
        if (!prevState) return prevState;

        const prevStateClone = { ...prevState };
        prevStateClone[valueClicked].isActive = prevState[valueClicked].isActive ? false : true;
        return prevStateClone;
      });
  };

  const metricNameMap = {
    occupancyRate: 'Occupancy Rate',
    adr: 'Daily Rate',
    revenue: 'Revenue',
    supply: 'Number of Listings',
    revPanChange: 'RevPAN Impact',
  };

  const isTabSelectedDataCurrency = tabSelected === 'adr' || tabSelected === 'revenue';

  const processDataString = (dateStr: string) => {
    const [year, month] = dateStr.split('-').map(Number);
    return shortMonthNames[month - 1] + ', ' + (year % 100);
  };

  const LTMTooltip = props => {
    const { active, payload, label } = props;
    if (active && payload && payload.length > 0) {
      // Customize the content of the tooltip
      const { date, metric } = payload[0].payload;

      return (
        <Box
          sx={{
            position: 'relative',
            width: '180px',
            background: '#fff',
            borderRadius: '4px',
            boxShadow: '0 2px 8px #0004',
            padding: '10px',
            pointerEvents: 'auto',
            top: '-75px',
            fontWeight: 500,
          }}
        >
          <Typography variant="body2" sx={{ marginBottom: '5px' }}>
            {processDataString(date)}
          </Typography>
          <Typography variant="body2">
            <span style={{ color: color }}>{metricNameMap[tabSelected]}: </span>
            {isTabSelectedDataCurrency && exchangeRates[currency].symbol}
            {abbreviateNumber(metric)}
            {!isTabSelectedDataCurrency && '%'}
          </Typography>
        </Box>
      );
    }

    return <></>;
  };

  const CustomTooltip = props => {
    const { active, payload, label, parentId, isRenderedFromOutside, handleMouseOut } = props;

    useEffect(() => {
      if (active && parent) {
        logAmplitudeEvent('tooltip_interacted', {
          tooltip_type: 'graph',
          parent: parent,
        });
      }
    }, [active]);

    const tabSelectedLTMMetricsMap = {
      occupancyRate: 'occupancy_rate_50',
      adr: 'adr_50',
      revenue: 'revenue_50',
    };

    const labelGraphTypeMap = {
      studio: 0,
      '6+': 6,
    };

    const rect = document.getElementById(parentId)?.getBoundingClientRect();
    const heightFromTop = rect ? rect.y : 0;
    let left: number = 0;
    let top: number = 0;

    if (isRenderedFromOutside) {
      const tooltipHeight = 160;
      const tooltipWidth = 350;

      // width adjustment
      left = Math.min(currentBarChartDataOnHover?.x, window.innerWidth - 10);
      if (left + tooltipWidth > window.innerWidth) {
        const dx = left + tooltipWidth - window.innerWidth;
        left = left - dx + 50;
      }
      left = Math.max(left - tooltipWidth / 2, 10);

      //height adjustment

      //Tooltip on top
      if (currentBarChartDataOnHover && currentBarChartDataOnHover.y - tooltipHeight - 200 >= 0) {
        top = rect ? rect.top - tooltipHeight - 15 : 0;
      }
      //Tooltip on bottom
      else {
        top = rect ? rect.top + tooltipHeight : 0;
      }

      top = Math.max(10, Math.min(top, window.innerHeight - 80));
    } else if (window.innerWidth < 800) {
      left = 80;
    } else if (rect && rect.x + rect.width > window.innerWidth - 50) {
      left = rect.width + rect.x - window.innerWidth + rect.width / 2;
    }

    const processLabelName = (label: string) => {
      return labelGraphTypeMap[label] != undefined ? labelGraphTypeMap[label] : label;
    };

    const findMaxMinGraphPoint = (
      formattedGraphArray: { date: string; metric: number }[] | null | undefined
    ) => {
      if (!formattedGraphArray) return { high: -1, low: -1 };

      let max = -Infinity;
      let min = Infinity;

      formattedGraphArray.forEach(graphData => {
        max = Math.max(max, graphData.metric);
        min = Math.min(min, graphData.metric);
      });

      return { high: max, low: min };
    };

    if (active && payload && payload.length > 0) {
      // Customize the content of the tooltip
      const { dataKey, value, unit } = payload[0];

      let tooltipSuffix = '';

      try {
        const percentageOfTotalListings = Math.round((value / totalListingsCount) * 100);
        tooltipSuffix = ` ( ${
          percentageOfTotalListings > 0 ? percentageOfTotalListings : ' less than 1'
        }% )`;
      } catch (err) {
        console.error('Failed to get tooltip suffix: ', err);
      }

      const formattedGraphArray =
        !isGraphLocked && isDisabled && showLTMGraphTooltip
          ? DEMO_DASHBOARD_FILTER_GRAPH_TOOLTIP_LTM_DATA[tabSelected]
          : graphTooltipLTMData
          ? Object.keys(graphTooltipLTMData[processLabelName(label)]).map(date => {
              return {
                date: date,
                metric:
                  graphTooltipLTMData[processLabelName(label)][date][
                    tabSelectedLTMMetricsMap[tabSelected]
                  ],
              };
            })
          : [];

      const { high, low } = findMaxMinGraphPoint(formattedGraphArray);

      const CustomizedDot = props => {
        const { cx, cy, stroke, payload, value } = props;
        const size = 6;

        if (value !== high && value !== low) return <></>;

        return (
          <svg x={cx - size} y={cy - size} width={size * 2} height={size * 2} fill="white">
            <g transform={`translate(${size} ${size})`}>
              <circle r={size} fill={color} />
              <circle r={size / 2} fill="white" />
            </g>
          </svg>
        );
      };

      return (
        // <div className="custom-tooltip">
        <ClickAwayListener
          onClickAway={() => {
            setCurrentBarChartDataOnHover(null);
          }}
        >
          <Box
            onMouseOut={handleMouseOut && handleMouseOut}
            onMouseEnter={() => {
              showLTMGraphTooltip && setIsLTMTooltipHovered(_ => true);
            }}
            sx={{
              ...{
                // position: 'absolute',
                position: 'relative',
                width: showLTMGraphTooltip ? '350px' : '260px',
                height: showLTMGraphTooltip ? '160px' : 'auto',
                background: '#fff',
                borderRadius: '4px',
                boxShadow: '0 2px 8px #0004',
                padding: '10px',
              },
              ...(!isRenderedFromOutside
                ? {
                    top: heightFromTop
                      ? heightFromTop < 320
                        ? '160px'
                        : (-1 * (rect as DOMRect).height) / (showLTMGraphTooltip ? 0.9 : 2.35)
                      : 'auto',
                    marginLeft: left ? -1 * left + 'px' : 'auto',
                  }
                : {
                    position: 'absolute',
                    zIndex: 9999999,
                    top: top + 'px',
                    left: left + 'px',
                  }),
            }}
          >
            <Grid container>
              {/* <Grid item>
                <Typography variant="h5" sx={{ fontSize: '14px' }}>
                  {catagoryMap !== 'dynamicRange'
                    ? catagoryMap[label]?.name
                      ? catagoryMap[label]?.name
                      : optionalCatagoryMapState
                      ? optionalCatagoryMapState[label]?.name
                      : ''
                    : title === 'Average Daily Rate'
                    ? processCurrencyGraphLabelRange(label, currency)
                    : label}{' '}
                  {tooltipTitleSuffix && tooltipTitleSuffix}
                </Typography>
              </Grid> */}
              {!isRenderedFromOutside && (
                <Grid container item xs={12}>
                  {isTabSelectedDataCurrency ? (
                    <Typography variant="h5" sx={{ fontSize: '15px', marginTop: '6px' }}>
                      <span style={{ fontWeight: 'bold', color: color }}>
                        {metricNameMap[dataKey]}:{' '}
                      </span>
                      {unit} {formatCurrency(convertCurrency(currency, value).toString())}
                    </Typography>
                  ) : (
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 500, fontSize: '15px', marginTop: '6px', color: color }}
                    >
                      <span style={{ color: COLOR_GREY_600 }}>
                        {/* {metricNameMap[dataKey]}:{' '} */}
                        {catagoryMap !== 'dynamicRange'
                          ? catagoryMap[label]?.name
                            ? catagoryMap[label]?.name
                            : optionalCatagoryMapState
                            ? optionalCatagoryMapState[label]?.name
                            : ''
                          : title === 'Average Daily Rate'
                          ? processCurrencyGraphLabelRange(label, currency)
                          : label}{' '}
                        {tooltipTitleSuffix && tooltipTitleSuffix}
                        {': '}
                      </span>
                      {value} {unit}
                      {tooltipSuffix}
                    </Typography>
                  )}
                </Grid>
              )}
              {isRenderedFromOutside && (
                <Grid container item xs={12}>
                  <Typography variant="h5" sx={{ fontSize: '15px', marginTop: '6px' }}>
                    <span style={{ fontWeight: 'bold', color: color }}>
                      {metricNameMap[tabSelected]}:
                    </span>{' '}
                    {isTabSelectedDataCurrency && exchangeRates[currency].symbol}
                    {formatCurrency(payload[0][tabSelected] + '')}
                    {!isTabSelectedDataCurrency && '%'}
                    {value}
                  </Typography>
                </Grid>
              )}
              {(graphTooltipLTMData || (!isGraphLocked && isDisabled && showLTMGraphTooltip)) && (
                <Grid container item xs={12} sx={{ marginTop: '10px', position: 'relative' }}>
                  <ResponsiveContainer width={'100%'} height={150} style={{ overflow: 'hidden' }}>
                    <LineChart
                      data={formattedGraphArray ? formattedGraphArray : data}
                      margin={{ top: 10, right: 15, left: -20, bottom: 35 }}
                      style={{ fontFamily: 'Roboto' }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tickLine={false}
                        fontSize={12}
                        ticks={[
                          formattedGraphArray[0].date,
                          formattedGraphArray[formattedGraphArray.length - 1].date,
                        ]}
                        tickFormatter={props => processDataString(props)}
                        tickMargin={10}
                        tickAlign={'start'}
                      />
                      <YAxis
                        fontSize={12}
                        tickLine={false}
                        tickFormatter={props =>
                          abbreviateNumber(
                            tabSelected === 'revenue' || tabSelected === 'adr'
                              ? convertCurrency(currency, props)
                              : props,
                            true
                          )
                        }
                      />
                      <Line
                        type="monotone"
                        dataKey={'metric'}
                        stroke={color}
                        strokeWidth={3}
                        dot={<CustomizedDot />}
                        isAnimationActive={false}
                      />
                      <Tooltip
                        isAnimationActive={false}
                        animationDuration={0}
                        allowEscapeViewBox={{ x: false, y: false }}
                        content={<LTMTooltip />}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  {!isGraphLocked && isDisabled && (
                    <PremiumOverlay
                      isXAxisTickVertical={isXAxisTickVertical}
                      offsetX={-10}
                      offsetWidth={40}
                      handleClick={() => {
                        handleOnSampleClick();
                      }}
                    />
                  )}
                </Grid>
              )}
            </Grid>
          </Box>
        </ClickAwayListener>
        // </div>
      );
    }

    return null;
  };

  return (
    <>
      <Grid
        container
        direction="column"
        justifyContent="center"
        item
        xs={12}
        // spacing={1}
        sx={{ boxShadow: '0 4px 10px #0003', borderRadius: 1, ...theme.utils.cardPadding }}
      >
        <Grid container item>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex' }}>
              <Typography variant="body1" className="subHeadings">
                <span style={titleHighlightType === 'prefix' ? { color: COLOR_PRIMARY_2 } : {}}>
                  {metricNameMap[tabSelected]}
                </span>{' '}
                by <span>{title}</span>{' '}
                <InfoButton
                  tooltipContent={
                    <Grid container direction="column" alignItems="flex-start">
                      <Typography variant="body2" style={{ fontWeight: 500, marginBottom: '5px' }}>
                        {tooltipText}
                      </Typography>
                      <Grid container item xs={12}>
                        <>
                          {catagoryMapState &&
                            catagoryMap === 'dynamicRange' &&
                            dynamicTooltipCatagory &&
                            Object.keys(catagoryMapState).map((catagoryMap, idx) => (
                              <Grid
                                item
                                xs={12}
                                key={idx}
                                direction={'row'}
                                sx={{ marginBottom: '2px' }}
                              >
                                <Typography variant="body2">
                                  <span style={{ fontWeight: 'bold' }}>
                                    {dynamicTooltipCatagory[idx] + ': '}
                                  </span>
                                  {processCurrencyGraphLabelRange(catagoryMap, currency)}
                                </Typography>
                              </Grid>
                            ))}
                        </>
                      </Grid>
                    </Grid>
                  }
                />
              </Typography>
            </Box>
          </Grid>

          {isLoading ? (
            <LoadingCard height={150} type="bars" />
          ) : (
            <>
              {showSelectionChips && (
                <Grid xs={12} item container>
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    justifyContent="flex-start"
                    alignItems="center"
                    gap={'10px'}
                    sx={{ width: '100%', marginTop: '5px', marginBottom: '0px' }}
                  >
                    {optionalCatagoryMapState &&
                      Object.keys(optionalCatagoryMapState).map(catagory => (
                        <FilterChip
                          title={optionalCatagoryMapState[catagory].name}
                          isActive={optionalCatagoryMapState[catagory].isActive}
                          color={color}
                          handleOnClick={() => handleFilterChipClick(catagory)}
                        />
                      ))}
                  </Stack>
                </Grid>
              )}
              {metricTabs.length > 1 && (
                <Grid container item xs={12} key={`tabs-container-${title}`}>
                  <Tabs
                    value={tabSelected}
                    // variant="scrollable"
                    // scrollButtons="auto"
                    // allowScrollButtonsMobile
                    sx={{
                      width: '100%',
                      '& .MuiTab-root.Mui-selected': {
                        color: color,
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: color,
                      },
                    }}
                    onChange={(e, value) => setTabSelected(value)}
                  >
                    {metricTabs.map(tab => (
                      <Tab label={metricNameMap[tab]} value={tab} wrapped />
                    ))}
                  </Tabs>
                </Grid>
              )}

              <Grid
                container
                item
                xs={12}
                sx={{ position: 'relative', paddingTop: '20px' }}
                id={'filter-graph-container' + title}
                ref={graphRef}
              >
                {data && (
                  <>
                    <ResponsiveContainer width="100%" height={isGraphSizeSmall ? 180 : 250}>
                      <BarChart
                        data={processGraphData(data)}
                        maxBarSize={maxBarSize ? maxBarSize : 20}
                        margin={{
                          left: tabSelected === 'revenue' ? 6 : tabSelected === 'adr' ? 0 : -15,
                          bottom: isXAxisTickVertical ? 40 : 10,
                        }}
                        style={{
                          fontFamily: 'Roboto',
                          position: 'relative',
                        }}
                        onMouseLeave={_ => {
                          setTimeout(() => {
                            setIsLTMTooltipHovered(prev => {
                              if (prev) {
                                return true;
                              } else {
                                setCurrentBarChartDataOnHover(null);
                                return false;
                              }
                            });
                          }, 300);
                        }}
                      >
                        {!isGraphSizeSmall ? (
                          <CartesianGrid strokeDasharray="3 3" />
                        ) : (
                          <CartesianGrid
                            vertical={false}
                            horizontal={true}
                            stroke={COLOR_GREY_400}
                            strokeDasharray="6 6"
                          />
                        )}
                        <XAxis
                          dataKey="name"
                          tickFormatter={props =>
                            optionalCatagoryMapState && optionalCatagoryMapState[props]
                              ? optionalCatagoryMapState[props].name
                              : catagoryMapState[props].name
                          }
                          tickLine={false}
                          axisLine={isGraphSizeSmall ? false : true}
                          angle={isXAxisTickVertical ? -90 : 0}
                          textAnchor={isXAxisTickVertical ? 'end' : 'middle'}
                          style={{
                            fontSize: isGraphSizeSmall ? '0.75rem' : '14px',
                            fontFamily: 'Roboto',
                          }}
                        >
                          {xAxisTitle && (
                            <Label
                              value={xAxisTitle}
                              offset={-5}
                              position="insideBottom"
                              style={{
                                fontSize: '0.75rem',
                                fontFamily: 'Roboto',
                              }}
                              fill={COLOR_GREY_600}
                            />
                          )}
                        </XAxis>
                        <YAxis
                          tickLine={false}
                          axisLine={isGraphSizeSmall ? false : true}
                          tickFormatter={props =>
                            (isTabSelectedDataCurrency ? exchangeRates[currency].symbol : '') +
                            abbreviateNumber(
                              isTabSelectedDataCurrency ? convertCurrency(currency, props) : props,
                              true
                            ) +
                            (tabSelected === 'occupancyRate' ? '%' : '')
                          }
                          style={{
                            fontSize: isGraphSizeSmall ? '0.75rem' : '14px',
                            fontFamily: 'Roboto',
                          }}
                        />
                        {!showLTMGraphTooltip && (
                          <Tooltip
                            isAnimationActive={false}
                            content={
                              <CustomTooltip
                                active
                                payload
                                label
                                parentId={'filter-graph-container' + title}
                              />
                            }
                            position={{ y: 0 }}
                          />
                        )}
                        <Bar
                          dataKey={tabSelected}
                          fill={color}
                          unit={
                            tabSelected === 'occupancyRate'
                              ? '%'
                              : tabSelected === 'supply'
                              ? 'listings'
                              : exchangeRates[currency].symbol
                          }
                          style={{ cursor: 'pointer' }}
                          radius={[100, 100, 100, 100]}
                          onMouseEnter={(data, idx, event) => {
                            showLTMGraphTooltip &&
                              setCurrentBarChartDataOnHover({
                                payload: data.payload,
                                x: event.clientX,
                                y: event.clientY,
                              });
                          }}
                          onClick={(data, idx, event) => {
                            showLTMGraphTooltip &&
                              setCurrentBarChartDataOnHover({
                                payload: data.payload,
                                x: event.clientX,
                                y: event.clientY,
                              });
                          }}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                    {isGraphLocked && (
                      <PremiumOverlay
                        isXAxisTickVertical={isXAxisTickVertical}
                        handleClick={() => {
                          handleOnSampleClick();
                        }}
                      />
                    )}
                  </>
                )}
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
      {currentBarChartDataOnHover && (
        <Portal>
          <CustomTooltip
            active={true}
            payload={[currentBarChartDataOnHover.payload]}
            label={currentBarChartDataOnHover.payload.name}
            parentId={'filter-graph-container' + title}
            isRenderedFromOutside={true}
            handleMouseOut={event => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                setCurrentBarChartDataOnHover(null);
              }
            }}
          />
        </Portal>
      )}
    </>
  );
};

export default React.memo(SingleBarFilterCard);
